import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code2,
  Eraser,
  Italic,
  Link2,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from 'lucide-react';
import { FormField } from 'components/forms/FormField';
import { ExpressionVariable } from './expression/ExpressionBuilder';
import { FORMATS, Position, useVariablesAutocomplete } from './useVariablesAutocomplete';

interface RichTextFieldProps {
  name: string;
  label?: string;
  /** HTML stocké côté back (l'ancien éditeur produisait déjà du HTML). */
  value: string;
  onChange: (html: string) => void;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  /** Message d'erreur du formulaire, affiché sous l'éditeur. */
  error?: string;
  /**
   * Variables du tunnel insérables comme jetons {{CODE}} : taper « {{ » ouvre une liste
   * filtrée au fil de la frappe. Absentes = pas d'autocomplétion.
   */
  variables?: ExpressionVariable[];
}

type Command =
  | { kind: 'exec'; command: string; value?: string; icon: JSX.Element; label: string }
  | { kind: 'link'; icon: JSX.Element; label: string };

const COMMANDS: Command[][] = [
  [
    { kind: 'exec', command: 'bold', icon: <Bold className="h-4 w-4" />, label: 'Gras' },
    { kind: 'exec', command: 'italic', icon: <Italic className="h-4 w-4" />, label: 'Italique' },
    {
      kind: 'exec',
      command: 'underline',
      icon: <Underline className="h-4 w-4" />,
      label: 'Souligné',
    },
    {
      kind: 'exec',
      command: 'strikeThrough',
      icon: <Strikethrough className="h-4 w-4" />,
      label: 'Barré',
    },
  ],
  [
    {
      kind: 'exec',
      command: 'justifyLeft',
      icon: <AlignLeft className="h-4 w-4" />,
      label: 'Aligner à gauche',
    },
    {
      kind: 'exec',
      command: 'justifyCenter',
      icon: <AlignCenter className="h-4 w-4" />,
      label: 'Centrer',
    },
    {
      kind: 'exec',
      command: 'justifyRight',
      icon: <AlignRight className="h-4 w-4" />,
      label: 'Aligner à droite',
    },
  ],
  [
    {
      kind: 'exec',
      command: 'insertUnorderedList',
      icon: <List className="h-4 w-4" />,
      label: 'Liste à puces',
    },
    {
      kind: 'exec',
      command: 'insertOrderedList',
      icon: <ListOrdered className="h-4 w-4" />,
      label: 'Liste numérotée',
    },
    { kind: 'link', icon: <Link2 className="h-4 w-4" />, label: 'Insérer un lien' },
    {
      kind: 'exec',
      command: 'removeFormat',
      icon: <Eraser className="h-4 w-4" />,
      label: 'Effacer la mise en forme',
    },
  ],
];

const BLOCK_FORMATS = [
  { value: 'p', label: 'Paragraphe' },
  { value: 'h2', label: 'Titre' },
  { value: 'h3', label: 'Sous-titre' },
];

/**
 * Éditeur de texte enrichi pour les descriptions de garantie.
 *
 * L'ancien back-office embarquait TinyMCE pour ce seul champ. On s'en tient ici à
 * `contentEditable` et aux commandes natives du navigateur : le contenu reste du HTML,
 * strictement compatible avec ce que le back stocke déjà, sans ajouter de dépendance ni
 * de clé d'API. La bascule « source » permet de reprendre à la main le HTML hérité, que
 * l'éditeur visuel ne sait pas toujours représenter proprement (classes Word, styles
 * en ligne).
 */
export const RichTextField: FC<RichTextFieldProps> = ({
  name,
  label,
  value,
  onChange,
  hint,
  required,
  disabled,
  error,
  variables,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isSourceMode, setIsSourceMode] = useState(false);
  const autocomplete = useVariablesAutocomplete(editorRef, variables, () =>
    onChange(editorRef.current?.innerHTML ?? '')
  );

  // On ne réécrit le DOM que si la valeur externe diffère réellement du contenu édité :
  // réécrire à chaque frappe replacerait le curseur en début de champ.
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && !isSourceMode && editor.innerHTML !== value) {
      editor.innerHTML = value ?? '';
    }
  }, [value, isSourceMode]);

  const exec = (command: string, commandValue?: string) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current?.innerHTML ?? '');
  };

  const insertLink = () => {
    const url = window.prompt('Adresse du lien');
    if (url) exec('createLink', url);
  };

  return (
    <FormField label={label} name={name} required={required} error={error}>
      <div className="overflow-hidden rounded-lg border border-gray-300 focus-within:border-primary">
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
          <select
            aria-label="Style de bloc"
            disabled={disabled || isSourceMode}
            onChange={(event) => exec('formatBlock', `<${event.target.value}>`)}
            defaultValue="p"
            className="mr-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 disabled:opacity-40"
          >
            {BLOCK_FORMATS.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>

          {COMMANDS.map((group, groupIndex) => (
            <div key={groupIndex} className="flex items-center gap-0.5 border-l border-gray-200 pl-1">
              {group.map((command) => (
                <ToolbarButton
                  key={command.label}
                  label={command.label}
                  icon={command.icon}
                  disabled={disabled || isSourceMode}
                  onClick={() =>
                    command.kind === 'link' ? insertLink() : exec(command.command, command.value)
                  }
                />
              ))}
            </div>
          ))}

          <div className="ml-auto">
            <ToolbarButton
              label={isSourceMode ? 'Revenir à l’éditeur' : 'Éditer le code source'}
              icon={<Code2 className="h-4 w-4" />}
              isActive={isSourceMode}
              disabled={disabled}
              onClick={() => setIsSourceMode((current) => !current)}
            />
          </div>
        </div>

        {isSourceMode ? (
          <textarea
            id={name}
            disabled={disabled}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            rows={8}
            className="w-full resize-y px-3 py-2 font-mono text-xs focus:outline-none disabled:bg-gray-100"
          />
        ) : (
          <div className="relative">
            <div
              id={name}
              ref={editorRef}
              contentEditable={!disabled}
              suppressContentEditableWarning
              onInput={(event) => {
                onChange((event.target as HTMLDivElement).innerHTML);
                autocomplete.detect();
              }}
              onKeyDown={(event) => autocomplete.onKeyDown(event)}
              onBlur={() => window.setTimeout(autocomplete.close, 150)}
              className="prose-sm min-h-[8rem] max-h-72 overflow-y-auto px-3 py-2 text-sm focus:outline-none [&_h2]:text-base [&_h2]:font-semibold [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            />
            {autocomplete.isOpen && autocomplete.position && (
              <SuggestionsPortal position={autocomplete.position}>
                {autocomplete.etape === 'format' ? (
                  <>
                    <li className="px-3 py-1 font-mono text-[11px] text-gray-400">
                      {autocomplete.variable?.code} · présentation
                    </li>
                    {FORMATS.map((format, index) => (
                      <li
                        key={format.suffixe || 'nombre'}
                        role="option"
                        aria-selected={index === autocomplete.activeIndex}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          autocomplete.choisirFormat(format.suffixe);
                        }}
                        onMouseEnter={() => autocomplete.setActiveIndex(index)}
                        className={`cursor-pointer px-3 py-1.5 text-gray-700 ${
                          index === autocomplete.activeIndex ? 'bg-primary/10' : ''
                        }`}
                      >
                        {format.libelle}
                      </li>
                    ))}
                  </>
                ) : (
                  autocomplete.variables.map((variable, index) => (
                    <li
                      key={variable.code}
                      role="option"
                      aria-selected={index === autocomplete.activeIndex}
                      // onMouseDown : le clic ne doit pas faire perdre le focus à l'éditeur avant l'insertion.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        autocomplete.choisirVariable(variable);
                      }}
                      onMouseEnter={() => autocomplete.setActiveIndex(index)}
                      className={`flex cursor-pointer items-baseline gap-2 px-3 py-1.5 ${
                        index === autocomplete.activeIndex ? 'bg-primary/10' : ''
                      }`}
                    >
                      <span className="font-mono text-xs text-primary">{variable.code}</span>
                      <span className="flex-1 truncate text-gray-700">{variable.libelle}</span>
                      {variable.unite && <span className="text-xs text-gray-400">{variable.unite}</span>}
                    </li>
                  ))
                )}
              </SuggestionsPortal>
            )}
          </div>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </FormField>
  );
};

const SUGGESTIONS_WIDTH = 288;
const SUGGESTIONS_MAX_HEIGHT = 240;

/**
 * Liste des suggestions rendue au niveau de la page, en position fixe sur le curseur :
 * le cadre de l'éditeur et la modale masquent ce qui dépasse de leur boîte, ce qui
 * cachait la liste dès que le curseur approchait du bas. Au-dessus du curseur quand la
 * place manque en dessous, et rabattue vers la gauche en bout de fenêtre.
 */
const SuggestionsPortal: FC<{ position: Position; children: ReactNode }> = ({ position, children }) => {
  const placeAbove = position.bottom + SUGGESTIONS_MAX_HEIGHT > window.innerHeight;
  const left = Math.min(position.left, window.innerWidth - SUGGESTIONS_WIDTH - 8);
  const style = placeAbove
    ? { left, bottom: window.innerHeight - position.top + 4 }
    : { left, top: position.bottom + 4 };

  return createPortal(
    <ul
      role="listbox"
      style={{ ...style, width: SUGGESTIONS_WIDTH, maxHeight: SUGGESTIONS_MAX_HEIGHT }}
      className="fixed z-[1000] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg"
    >
      {children}
    </ul>,
    document.body
  );
};

const ToolbarButton: FC<{
  label: string;
  icon: JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
}> = ({ label, icon, onClick, disabled, isActive }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    // onMouseDown plutôt que onClick : le clic ne doit pas voler le focus à la sélection
    // en cours, sinon la commande s'applique à un texte vide.
    onMouseDown={(event) => {
      event.preventDefault();
      onClick();
    }}
    className={`rounded p-1.5 transition-colors disabled:opacity-40 ${
      isActive ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-200'
    }`}
  >
    {icon}
  </button>
);
